import GAME_IDS from 'a/hub/data/GameIds';
import { SEARCHLIGHT_EVENT_TYPE } from 'root/lib/SearchlightDispatcher';

import { SEARCHLIGHT_ACTION_TYPE, dispatchEvent } from './searchlightActionCreators';

const { METAGAME } = GAME_IDS;

export const initSearchlightDispatcher = keycloakSessionId => ({
  type: SEARCHLIGHT_ACTION_TYPE.INIT_SEARCHLIGHT_DISPATCHER,
  payload: keycloakSessionId,
});

export const setUserId = userId => ({
  type: SEARCHLIGHT_ACTION_TYPE.SET_USER_ID,
  payload: userId,
});

export const reportLoginEvent = (status, data = {}) => ({
  type: SEARCHLIGHT_ACTION_TYPE.REPORT_LOGIN_EVENT,
  payload: { status, data },
});

export const reportMetric = (metricId, metricType, value, product) => ({
  type: SEARCHLIGHT_ACTION_TYPE.REPORT_METRIC,
  payload: { metricId, metricType, value, product },
});

export const setIdentity = (identity) => ({
  type: SEARCHLIGHT_ACTION_TYPE.SET_IDENTITY,
  payload: identity,
});

export const setSkillMap = (skillMap) => ({
  type: SEARCHLIGHT_ACTION_TYPE.SET_SKILL_MAP,
  payload: skillMap,
});

export const setSourceId = (isFieldTrial) => ({
  type: SEARCHLIGHT_ACTION_TYPE.SET_SOURCE_ID,
  payload: isFieldTrial,
});

export const changeGameSession = gameSessionId => ({
  type: SEARCHLIGHT_ACTION_TYPE.SEARCHLIGHT_CHANGE_GAME_SESSION,
  payload: gameSessionId,
});

export const reportBackOnline = (gameId, details) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.REPORT_BACK_ONLINE, gameId, details);

export const reportBonusGameComplete = (gameId, details) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.BONUS_GAME_COMPLETE, gameId, details);

export const reportBonusGameStart = (gameId, details) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.BONUS_GAME_START, gameId, details);

export const reportEasterEggFound = (gameId, details) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.EASTER_EGG_FOUND, gameId, details);

export const reportLoadGame = (gameId, details) => dispatchEvent(SEARCHLIGHT_EVENT_TYPE.LOAD_GAME, gameId, details);

export const reportSelection = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.SELECTION, gameId, details, logMessage);

// original use of this function just passed roundSessionId in the last argument as a string, more recent uses send that and more
// in an object. If it's a
export const reportRoundStart = (gameId, details, logMessage, searlightParam) => {
  const searchlight = searlightParam || {};
  return dispatchEvent(
    SEARCHLIGHT_EVENT_TYPE.ROUND_START,
    gameId,
    details,
    logMessage,
    typeof searchlight === 'string' ? { roundSessionId: searchlight } : searchlight,
  );
};

export const reportRoundComplete = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.ROUND_COMPLETE, gameId, details, logMessage);

export const reportLevelComplete = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.LEVEL_COMPLETE, gameId, details, logMessage);

export const reportGameComplete = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.GAME_COMPLETE, gameId, details, logMessage);

export const reportPractice = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.PRACTICE, gameId, details, logMessage);

export const reportHelp = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.HELP, gameId, details, logMessage);

export const reportReadAloud = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.READ_ALOUD, gameId, details, logMessage);

export const reportInstruction = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.INSTRUCTION, gameId, details, logMessage);

export const reportExit = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.EXIT_GAME, gameId, details, logMessage);

export const reportError = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.ERROR, gameId, details, logMessage);

export const reportRepeatOnboarding = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.REPEAT_ONBOARDING, gameId, details, logMessage);

export const reportRepeatQuestion = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.REPEAT_QUESTION, gameId, details, logMessage);

export const reportSelectField = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.SELECT_FIELD, gameId, details, logMessage);

export const reportOnboardingStart = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.ONBOARDING_START, gameId, details, logMessage);

export const reportOnboardingComplete = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.ONBOARDING_COMPLETE, gameId, details, logMessage);

export const reportRWOpen = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.RW_OPEN, gameId, details, logMessage);

export const reportRWClose = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.RW_CLOSE, gameId, details, logMessage);

export const reportRWAudioButton = (gameId, details, logMessage) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.RW_AUDIO_BUTTON, gameId, details, logMessage);

export const reportLogOut = (origin = 'Logout Modal', originGroup, trigger = 'Log out icon', triggerType = 'manual') =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.LOG_OUT, METAGAME, {
    gameId: METAGAME,
    origin,
    originGroup,
    trigger,
    triggerType,
  });

export const reportSessionRefresh = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.SESSION_REFRESH, gameId, details);

export const reportPageTurn = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.PAGE_TURN, gameId, details);

export const reportOpenTableOfContents = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.OPEN_TOC, gameId, details);

export const reportNavigateTableOfContents = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.NAV_TOC, gameId, details);

export const reportCloseTableOfContents = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.CLOSE_TOC, gameId, details);

export const reportStartReadingMode = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.START_READING_MODE, gameId, details);

export const reportExitReadingMode = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.EXIT_READING_MODE, gameId, details);

export const reportStartChapter = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.START_CHAPTER, gameId, details);

export const reportExitChapter = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.EXIT_CHAPTER, gameId, details);

export const reportStartActivity = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.START_ACTIVITY, gameId, details);

export const reportExitActivity = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.EXIT_ACTIVITY, gameId, details);

export const reportChangeSettings = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.SETTING, gameId, details);

export const reportReaderChangeSettings = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.CHANGE_READER_SETTINGS, gameId, details);

export const reportMultipleSessions = (gameId, details = {}) =>
  dispatchEvent(SEARCHLIGHT_EVENT_TYPE.MULTIPLE_SESSIONS, gameId, details);
